import { useState } from 'react';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

import { InitialAvatarComponent } from '@workinance/shared/ui/InitialAvatarComponent';
import { ModalLayoutComponent } from '@workinance/shared/ui/ModalLayoutComponent';
import { StatusPillComponent } from '@workinance/shared/ui/StatusPillComponent';

import { useAuthStore } from '@/store/auth.store';

interface ISwitchBusinessModalComponentProps {
	isOpen: boolean;
	onClose: () => void;
}

/** Lists the owner's businesses so they can pick which one the dashboard works in. New businesses are created in the admin app. */
export function SwitchBusinessModalComponent({
	isOpen,
	onClose,
}: ISwitchBusinessModalComponentProps) {
	const businesses = useAuthStore((state) => state.businesses);
	const activeBusinessId = useAuthStore((state) => state.business?.id);
	const switchBusiness = useAuthStore((state) => state.switchBusiness);
	const [switchingToId, setSwitchingToId] = useState<string | null>(null);

	const handleSelect = async (businessId: string) => {
		setSwitchingToId(businessId);
		// The page reloads into the new business, so nothing needs resetting afterwards.
		await switchBusiness(businessId);
	};

	return (
		<ModalLayoutComponent
			isOpen={isOpen}
			onClose={onClose}
			title="Switch business"
			description="Choose the business you want to work in."
		>
			<ul className="-mx-2">
				{businesses.map((business) => {
					const isActive = business.id === activeBusinessId;
					const isDisabled = isActive || switchingToId !== null;
					return (
						<li key={business.id}>
							<button
								type="button"
								onClick={() => void handleSelect(business.id)}
								disabled={isDisabled}
								aria-current={isActive ? 'true' : undefined}
								className={parseClassnames(
									'flex w-full items-center gap-4 rounded-2xl px-2 py-3 text-left transition-colors',
									{
										'cursor-default': isDisabled,
										'cursor-pointer hover:bg-brand-soft/60': !isDisabled,
									},
								)}
							>
								<InitialAvatarComponent name={business.name} />
								<div className="min-w-0 flex-1">
									<p className="truncate font-semibold">{business.name}</p>
									<p className="truncate font-code text-xs text-mute">
										Code {business.businessCode}
									</p>
								</div>
								{isActive ? (
									<StatusPillComponent tone="open">Active</StatusPillComponent>
								) : (
									<span className="shrink-0 text-sm font-semibold text-brand">
										{switchingToId === business.id ? 'Opening...' : 'Select'}
									</span>
								)}
							</button>
						</li>
					);
				})}
			</ul>
		</ModalLayoutComponent>
	);
}
